// Lightweight visitor tracker — fires one pageview event per route change.
// Persists a stable `visitor_id` in localStorage and a `session_id` in
// sessionStorage so aggregates on the backend can distinguish unique
// visitors, sessions, and repeat pageviews.
//
// Skipped for /admin/* paths so admin activity never inflates public stats.

const API_URL = process.env.REACT_APP_BACKEND_URL;
const VISITOR_KEY = "dmm_visitor_id";
const SESSION_KEY = "dmm_analytics_session";

const uid = () =>
  (typeof crypto !== "undefined" && crypto.randomUUID
    ? crypto.randomUUID()
    : Math.random().toString(36).slice(2) + Date.now().toString(36));

const getVisitorId = () => {
  try {
    let v = localStorage.getItem(VISITOR_KEY);
    if (!v) {
      v = uid();
      localStorage.setItem(VISITOR_KEY, v);
    }
    return v;
  } catch {
    return uid();
  }
};

const getSessionId = () => {
  try {
    let s = sessionStorage.getItem(SESSION_KEY);
    if (!s) {
      s = uid();
      sessionStorage.setItem(SESSION_KEY, s);
    }
    return s;
  } catch {
    return uid();
  }
};

export const trackPageview = (path) => {
  try {
    // Never track admin routes.
    if (!path || path.startsWith("/admin")) return;
    if (!API_URL) return;

    const body = JSON.stringify({
      path,
      referrer: document.referrer || "",
      session_id: getSessionId(),
      visitor_id: getVisitorId(),
      screen_w: window.screen?.width || 0,
      screen_h: window.screen?.height || 0,
    });

    // sendBeacon-first for reliability on route change / tab close.
    if (navigator.sendBeacon) {
      const blob = new Blob([body], { type: "application/json" });
      const ok = navigator.sendBeacon(`${API_URL}/api/analytics/track`, blob);
      if (ok) return;
    }
    // Fallback: fire-and-forget fetch, keepalive so it survives unload.
    fetch(`${API_URL}/api/analytics/track`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body,
      keepalive: true,
    }).catch(() => {});
  } catch {
    /* silently swallow — analytics must never break the site */
  }
};
